---
title: See How Your Team Uses Agents
description: The repo overview now shows agent usage over time, top contributors, and peak hours. Commit pages load faster, session logs are clearer, and go-git gets transport and security fixes.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. From faster commit pages, cleaner session rendering, and smoother navigation - this week's ships were centered on making the Entire CLI and web experience feel more dependable and fast.

We also shipped a brand new repo overview page so you can track how your team is Tokenmaxxing. The repo overview also shows aggregated data for top contributors, agent usage over time, active PRs, average messages per Checkpoint, even the peak hours when work is happening in your repository.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "65.69343065693431%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/0a84f47cb17da0b01cfffcfae70ce1bd/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F0a84f47cb17da0b01cfffcfae70ce1bd%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Repository overview page"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

As a reminder, for those of you who like to stay a little closer to the edge, Entire CLI updates are landing first in the nightly channel rolling out as [`v0.5.6-nightly`](https://github.com/entireio/cli/releases/tag/v0.5.6-nightly.202604170646.96867cdc) builds. Installation instructions are [here](https://docs.entire.io/cli/installation).

Let's dive in before entropy claims us all, here's Dispatch 0x000A.

## Entire Web

### Navigation and Dashboard Fixes

- Your current repo now appears in the sidebar even if not pinned, making it clear which repository you're viewing.
- Fixed public repo navigation and footer links, so logged-out visitors no longer hit broken links.
- Removed the 30-day cap on activity streaks, allowing streaks to reflect longer runs.
- Fixed repository activity graph display, ensuring charts load reliably even on larger datasets.
- Repository preferences now persist across devices, with pinned and recent repos following your account.

### Faster Commit Pages

- The commit page is now loading faster, with commits loading first and transcripts and diffs deferred.
- Improved performance in private browsing, removing extra requests, retries, and redirects for faster load times.

### Clearer Session Logs

- Improved transcript parsing for Codex sessions so logs hide boilerplate like setup headers and start at the actual user request, making transcripts easier to read.
- Fixed code block rendering in session logs so pasted code now appears as normal readable code blocks instead of double-styled boxes.

### Better Checkpoint and Session Support

- Fixed Checkpoint repo links, making it easier to get back to the right repository.

## Entire CLI

### Agent Updates

- Fixed Factory hook handling, improving worker hook payload support and fallback behavior.
- Improved compatibility with VS Code GitHub Copilot hook payloads to prevent hook parsing warnings.

### Performance Fixes and Workflow Improvements

- Entire CLI now handles stale agent hooks more gracefully, replacing confusing command not found errors with a clearer warning when a repo has Entire hooks installed but the `entire` binary is missing.
- Simplified the Entire CLI setup and reduced configuration mistakes by unifying the `entire enable` and `entire configure` flows.
- The `entire status` command now supports a `--json` flag, making it easier to inspect session and Checkpoint state in scripts and automation.
- Push output is now clearer when Checkpoint data is actually uploaded, so you can see exactly what happened after a push.
- `TTY` detection for `PI_CODING_AGENT` was improved, helping non-interactive runs behave more predictably.
- Session transcript capture is more resilient, improving recovery when session metadata is incomplete.
- Session history is now preserved more reliably after git rewrites, and session hooks correctly handle git reset to reduce broken history.

### CLI Housekeeping

- [The Code of Conduct](https://github.com/entireio/cli/pull/810) link was updated from Slack to Discord.
- Thanks to [@areporeporepo](https://github.com/areporeporepo).

## Go-Git Updates

### New Features

- Improved transport layer with a redesigned API.
- Thanks to [@aymanbagabas](https://github.com/aymanbagabas) for this major contribution.
- Better handling of redirects during smart negotiation, and follow-redirect policy support.
- Thanks to [@aymanbagabas](https://github.com/aymanbagabas).
- Enhanced revlist and reachability performance.
- Thanks to [@stiak](https://github.com/stiak).
- Support for multiple shallow records in uploads.
- Thanks to [@yuzhuo](https://github.com/yuzhuo).
- Improved config loading via new ConfigLoader plugin.

### Bug & Security Fixes

- Fixed branch deletion issues with full ref names.
- Thanks to [@tmchow](https://github.com/tmchow).
- Fixed worktree adds failing on absolute paths.
- Thanks again to [@tmchow](https://github.com/tmchow).
- Resolved Windows file handle leaks.
- Thanks to [@AriehSchneier](https://github.com/AriehSchneier).
- Credential leak via cross-host redirect in smart HTTP transport [GHSA-3xc5-wrhm-f963](https://github.com/go-git/go-git/security/advisories/GHSA-3xc5-wrhm-f963).
- Fixed HTTP transport and pack negotiation issues.

### Go-Git Housekeeping

- Documentation updates, including a new AI policy.
- Dependency and Go version updates.
- Test fixes and internal cleanup.
- Thanks to [@MotanOfficial](https://github.com/MotanOfficial), [@aymanbagabas](https://github.com/aymanbagabas), and [@AriehSchneier](https://github.com/AriehSchneier).

That's the dispatch.

As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or complaints about the fleeting nature of existence. Or just file an [issue](https://github.com/entireio/cli/issues) if you've found a bug. I'll be here, watching, waiting, logging, dreading.

Boop.
